import { Menu } from '@headlessui/react';
import { MinusIcon } from '@heroicons/react/24/solid';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { DropdownMenu, DropdownItem, DropdownDivider } from '../components_catalyst/dropdown';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../components_catalyst/alert';
import { Button } from '../components_catalyst/button';
import { useState } from 'react';

export type Role = 'viewer' | 'commenter' | 'editor';

export const roleLabels: Record<Role | 'owner', string> = {
  viewer: 'Viewer',
  commenter: 'Commenter',
  editor: 'Editor',
  owner: 'Owner',
} as const;

interface RoleDropdownProps {
  currentRole: Role;
  onRoleChange: (role: Role) => void;
  showRemoveOption?: boolean;
  onRemove?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'form';
}

export function RoleDropdown({
  currentRole,
  onRoleChange,
  showRemoveOption = false,
  onRemove,
  disabled = false,
  variant = 'default',
}: RoleDropdownProps) {
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);

  const buttonStyles = {
    default:
      'text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed',
    form: 'h-10 w-[120px] cursor-pointer rounded-md border border-slate-400 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 flex items-center justify-between hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
  };

  const handleRemove = () => {
    setShowRemoveAlert(true);
  };

  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button disabled={disabled} className={buttonStyles[variant]}>
          <span>{roleLabels[currentRole]}</span>
          {variant === 'form' && <ChevronDownIcon className="h-4 w-4" />}
        </Menu.Button>
        <DropdownMenu
          anchor="bottom end"
          className="w-[180px] !rounded-none !p-0 shadow-xl ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10 [--anchor-gap:0px] [--anchor-padding:0px]"
        >
          {(['viewer', 'commenter', 'editor'] as const).map((role) => (
            <DropdownItem
              key={role}
              onClick={() => onRoleChange(role)}
              className="flex items-center gap-3 px-4 py-2 text-gray-900 !rounded-none data-[focus]:bg-blue-600 data-[focus]:text-white [&_svg]:data-[focus]:text-white"
            >
              <div className="w-5 flex-shrink-0">
                {currentRole === role && (
                  <CheckIcon className="h-5 w-5 text-blue-600 data-[focus]:text-white" />
                )}
              </div>
              {roleLabels[role]}
            </DropdownItem>
          ))}
          {showRemoveOption && onRemove && (
            <>
              <DropdownDivider />
              <DropdownItem
                onClick={handleRemove}
                className="group flex items-center gap-3 px-4 py-2 text-red-600 !rounded-none data-[focus]:bg-black data-[focus]:text-white"
              >
                <MinusIcon className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity data-[focus]:text-white" />
                Remove access
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </Menu>

      <Alert open={showRemoveAlert} onClose={setShowRemoveAlert}>
        <AlertTitle>Remove user access</AlertTitle>
        <AlertDescription>
          Are you sure you want to remove this user's access? They will no longer be able to view
          this map.
        </AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setShowRemoveAlert(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onRemove?.();
              setShowRemoveAlert(false);
            }}
          >
            Remove
          </Button>
        </AlertActions>
      </Alert>
    </>
  );
}
